import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Bug, RefreshCcw, ChevronLeft, Clock, User, Building2,
  Globe, Monitor, Image, AlertTriangle, CheckCircle,
  Trash2, Eye, Flame, Info, AlertCircle, ExternalLink
} from 'lucide-react';

const BugReportsPage = ({ onMenuClick }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/bug-reports/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSeverity !== 'all') params.append('severity', filterSeverity);
      const res = await api.get(`/bug-reports?${params}`);
      setReports(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity]);

  useEffect(() => {
    fetchStats();
    fetchReports();
  }, [fetchStats, fetchReports]);

  const fetchReportDetail = useCallback(async (id) => {
    try {
      setDetailLoading(true);
      const res = await api.get(`/bug-reports/${id}`);
      setSelectedReport(res.data);
      setAdminNotes(res.data.admin_notes || '');
    } catch (err) {
      toast.error('Erreur lors du chargement du rapport');
      navigate('/admin/bug-reports');
    } finally {
      setDetailLoading(false);
    }
  }, [navigate]);

  // Load the detail whenever the URL carries a report id (deep-linkable / new tab).
  useEffect(() => {
    if (reportId) {
      fetchReportDetail(reportId);
    } else {
      setSelectedReport(null);
    }
  }, [reportId, fetchReportDetail]);

  const openReport = (id) => navigate(`/admin/bug-reports/${id}`);

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedReport) return;
    setUpdating(true);
    try {
      const res = await api.put(`/bug-reports/${selectedReport.id}/status`, {
        status: newStatus,
        admin_notes: adminNotes || null,
      });
      setSelectedReport(res.data);
      toast.success('Statut mis à jour');
      fetchReports();
      fetchStats();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedReport) return;
    setUpdating(true);
    try {
      const res = await api.put(`/bug-reports/${selectedReport.id}/status`, {
        status: selectedReport.status,
        admin_notes: adminNotes,
      });
      setSelectedReport(res.data);
      toast.success('Notes enregistrées');
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport de bug ?')) return;
    try {
      await api.delete(`/bug-reports/${id}`);
      toast.success('Rapport de bug supprimé');
      setSelectedReport(null);
      navigate('/admin/bug-reports');
      fetchReports();
      fetchStats();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getSeverityBadge = (severity) => {
    const config = {
      low: { label: 'Basse', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: Info },
      medium: { label: 'Moyenne', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: AlertCircle },
      high: { label: 'Haute', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: AlertTriangle },
      critical: { label: 'Critique', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: Flame },
    };
    const c = config[severity] || config.medium;
    const Icon = c.icon;
    return (
      <Badge className={`${c.className} gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: 'Ouvert', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      in_review: { label: 'En revue', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
      in_progress: { label: 'En cours', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
      resolved: { label: 'Résolu', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
      closed: { label: 'Fermé', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
    };
    const c = config[status] || config.open;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getRoleBadge = (role) => {
    const config = {
      student: { label: 'Étudiant', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
      trainer: { label: 'Formateur', className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
      cfa_admin: { label: 'Admin CFA', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
      super_admin: { label: 'Super Admin', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
    };
    const c = config[role] || { label: role, className: 'bg-slate-100 text-slate-600' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // ─── Detail View ────────────────────────────────────────
  if (reportId) {
    if (detailLoading || !selectedReport) {
      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex justify-center items-center">
          <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      );
    }
    const r = selectedReport;
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <header className="glass-header sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/bug-reports')}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white truncate font-outfit">
                Bug #{r.id} : {r.title}
              </h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => handleDelete(r.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            {getStatusBadge(r.status)}
            {getSeverityBadge(r.severity)}
            {getRoleBadge(r.user_role)}
            <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {formatDate(r.created_at)}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{r.description}</p>
                </CardContent>
              </Card>

              {r.steps_to_reproduce && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Étapes pour reproduire</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{r.steps_to_reproduce}</p>
                  </CardContent>
                </Card>
              )}

              {r.expected_behavior && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Comportement attendu</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{r.expected_behavior}</p>
                  </CardContent>
                </Card>
              )}

              {r.actual_behavior && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Comportement observé</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{r.actual_behavior}</p>
                  </CardContent>
                </Card>
              )}

              {r.screenshot_url && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Image className="w-4 h-4" /> Capture d'écran
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${r.screenshot_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${r.screenshot_url}`}
                        alt="Capture du bug"
                        className="rounded-lg border border-slate-200 dark:border-slate-700 max-h-80 w-auto cursor-pointer hover:opacity-90 transition-opacity"
                      />
                    </a>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Rapporteur</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {r.user && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                        <User className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">
                          {r.user.first_name} {r.user.last_name}
                        </p>
                        <p className="text-xs text-slate-500">{r.user.email}</p>
                      </div>
                    </div>
                  )}
                  {r.organization && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <Building2 className="w-4 h-4" />
                      {r.organization.name}
                    </div>
                  )}
                  {r.page_url && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <Globe className="w-4 h-4 shrink-0" />
                      <span className="truncate">{r.page_url}</span>
                    </div>
                  )}
                  {r.browser_info && (
                    <div className="flex items-start gap-2 text-xs text-slate-500">
                      <Monitor className="w-4 h-4 shrink-0 mt-0.5" />
                      <span className="break-all line-clamp-3">{r.browser_info}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Gestion</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Statut</label>
                    <Select
                      value={r.status}
                      onValueChange={handleUpdateStatus}
                      disabled={updating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Ouvert</SelectItem>
                        <SelectItem value="in_review">En revue</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="resolved">Résolu</SelectItem>
                        <SelectItem value="closed">Fermé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Notes admin</label>
                    <Textarea
                      placeholder="Notes internes sur ce bug..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={4}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSaveNotes}
                      disabled={updating}
                      className="w-full"
                    >
                      Enregistrer les notes
                    </Button>
                  </div>

                  {r.resolved_at && (
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      Résolu le {formatDate(r.resolved_at)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="glass-header sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onMenuClick} className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-indigo-950 dark:text-white font-outfit flex items-center gap-2">
                <Bug className="w-7 h-7 text-rose-500" />
                Rapports de bugs
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">Suivez et gérez les problèmes signalés par les utilisateurs</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchReports(); fetchStats(); }}>
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-l-4 border-l-slate-400">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Ouverts</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.by_status.open}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-yellow-600 uppercase tracking-wide">En cours</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {stats.by_status.in_review + stats.by_status.in_progress}
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Résolus</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.by_status.resolved}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Critiques</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.by_severity.critical}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="open">Ouvert</SelectItem>
                  <SelectItem value="in_review">En revue</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="resolved">Résolu</SelectItem>
                  <SelectItem value="closed">Fermé</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="Sévérité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les sévérités</SelectItem>
                  <SelectItem value="critical">Critique</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="low">Basse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16">
            <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
              <p className="text-lg font-medium text-slate-900 dark:text-white">Aucun rapport de bug</p>
              <p className="text-sm text-slate-500 mt-1">
                {filterStatus !== 'all' || filterSeverity !== 'all'
                  ? 'Essayez d\'ajuster vos filtres'
                  : 'Aucun bug n\'a été signalé pour le moment'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <Card
                key={report.id}
                className="cursor-pointer hover:shadow-md hover:border-violet-200 dark:hover:border-violet-800 transition-all duration-200"
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    window.open(`/admin/bug-reports/${report.id}`, '_blank');
                  } else {
                    openReport(report.id);
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-slate-400">#{report.id}</span>
                        <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                          {report.title}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">
                        {report.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {getStatusBadge(report.status)}
                        {getSeverityBadge(report.severity)}
                        {getRoleBadge(report.user_role)}
                        {report.organization && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {report.organization.name}
                          </span>
                        )}
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(report.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {report.screenshot_url && (
                        <div className="w-8 h-8 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center" title="Capture jointe">
                          <Image className="w-4 h-4 text-slate-400" />
                        </div>
                      )}
                      <button
                        type="button"
                        title="Ouvrir dans un nouvel onglet"
                        className="w-8 h-8 rounded flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/admin/bug-reports/${report.id}`, '_blank');
                        }}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <Eye className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default BugReportsPage;
